import React = require('react');
import _ = require('underscore');
import { ButtonType } from '@tableau/widgets';

import { ToasterService,ToastType } from '~commonInfra/services/ToasterService';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/commonInfra/components/ButtonWithSpinner';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { UserErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { DefaultSiteRole } from '../../../services/resources/SiteRoleOptions';
import { SiteRoles } from '../../../users/serverApi/types/ISiteRole';
import { Groups } from '../../Groups';
import { IGroupInfo } from '../../models/IGroupInfo';
import { GrantLicenseModes } from '../../serverApi/requests/SyncActiveDirectoryGroupRequest';
import { editGroupDialogBodyStyles } from '../styles/EditGroupDialogStyles';
import { EditEphemeralUserField } from './EditEphemeralUserField';
import { EditGrantLicenseModeField } from './EditGrantLicenseModeField';
import { EditGroupNameField } from './EditGroupNameField';

export interface EditGroupDialogProps {
  readonly close: (result: any) => void;
  readonly dismiss: () => void;
  readonly groupInfo: IGroupInfo;
}

export function EditGroupDialog(props: EditGroupDialogProps): JSX.Element  {

  let initialSiteRole: string = props.groupInfo.minimumSiteRole && props.groupInfo.minimumSiteRole !== SiteRoles.Unlicensed ?
    props.groupInfo.minimumSiteRole : DefaultSiteRole;

  const [name, setName] = React.useState(props.groupInfo.name);
  const [externalUserEnabled, setExternalUserEnabled] = React.useState(props.groupInfo.externalUserEnabled);
  const [siteRole, setSiteRole] = React.useState(initialSiteRole);
  const [grantLicenseMode, setGrantLicenseMode] = React.useState(props.groupInfo.grantLicenseMode as GrantLicenseModes);
  const [busy, setBusy] = React.useState(false);
  const [groupNameError, setGroupNameError] = React.useState(undefined);


  const updateGroupCallback = async () => {
    setBusy(true);
    const siteRoleApiValue = grantLicenseMode === GrantLicenseModes.ON_LOGIN ? siteRole : SiteRoles.Unlicensed;
    const result = await Groups.updateGroup(props.groupInfo.id, name,
      siteRoleApiValue, grantLicenseMode, externalUserEnabled);
    if (!!result.errors && result.errors.length > 0) {
      handleErrors(result.errors[0]);
    } else {
      const successMsg = Messages.editGroup_success({ NEW_ITEM_NAME: L10n.elideIfLong(props.groupInfo.name) });
      ToasterService.instance.pop({ type: ToastType.Success, text: successMsg });
      props.close({});
    }
  };

  const handleErrors = (error: IError) => {
    const errorMsg = errorMessageFor(error.code);
    if (!errorMsg) {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unknownFailure() });
      props.close({});
    } else {
      setBusy(false);
      setGroupNameError(errorMsg);
    }
  };

  const errorMessageFor = (errorCode: number) => {
    switch (errorCode) {
      case UserErrorCode.UserGroupInvalidName:
        return Messages.errorUserGroupInvalidName();
      case UserErrorCode.UserGroupWithNameAlreadyExists:
        return Messages.errorUserGroupWithNameAlreadyExists();
      case UserErrorCode.UserAllUsersGroupAlreadyExists:
        return Messages.errorUserGroupWithNameAlreadyExists();
      default:
        return undefined;
    }
  };

  const canUpdateGroup = (): boolean => {
    return ((props.groupInfo.name !== name)
    || (props.groupInfo.externalUserEnabled !== externalUserEnabled)
    || (initialSiteRole !== siteRole && grantLicenseMode === GrantLicenseModes.ON_LOGIN)
    || (props.groupInfo.grantLicenseMode !== grantLicenseMode)) && name.length > 0;
  };


  const isExternalUserEnabledForSite = getSessionManagementServiceInstance().isExternalUserEnabledForSite();
  const canManageUsers = SessionUserSelectors.canManageUsers(getAppStore().getState());

  return (
    <div>
      <div style={editGroupDialogBodyStyles}>
        {<EditGroupNameField
            name={name}
            groupNameError={groupNameError}
            setName={setName}
            setGroupNameError={setGroupNameError}/>}
        {isExternalUserEnabledForSite && canManageUsers &&
          <EditEphemeralUserField
            externalUserEnabled={externalUserEnabled}
            setExternalUserEnabled={setExternalUserEnabled} />}
        { canManageUsers &&
          <EditGrantLicenseModeField
            siteRole={siteRole}
            grantLicenseMode={grantLicenseMode}
            setSiteRole={setSiteRole}
            setGrantLicenseMode={setGrantLicenseMode}
          />
        }
      </div>
       <div className="tb-dialog-actions">
        <ButtonWithSpinner
          buttonType={ButtonType.Outline}
          onClick={props.dismiss}
          testId={'confirm-action-dialog-cancel'}
          children={Messages.cancel()}
          disabled={busy}
        />
        <span className="tb-margin-left-wide">
          <ButtonWithSpinner
            buttonType={ButtonType.Primary}
            showSpinner={busy}
            onClick={updateGroupCallback}
            testId={'confirm-action-dialog-submit'}
            children={Messages.save()}
            disabled={!canUpdateGroup()}
          />
        </span>
      </div>
    </div>
  );
}
